import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Bell,
  Settings,
  Home,
  Target,
  Search,
  TrendingUp,
  Plus,
} from "lucide-react";
import { useNotification } from "../../context/NotificationContext";
import websocketService from "../../services/websocket";

const FeedNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotificationDot } = useNotification();
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  // Logic: Correctly identify the active tab for styling
  const activeTab = useMemo(() => {
    const path = location.pathname;
    const search = location.search;
    if (path === "/explore") return "explore";
    if (path === "/feed" && search.includes("type=for_you")) return "for-you";
    if (path === "/home") return "home";
    if (path === "/trending") return "trending";
    return "";
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleResize = () => setWindowSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const showToggle = useMemo(() => {
    const path = location.pathname;
    const search = location.search;
    return (
      path === "/explore" ||
      (path === "/feed" && search.includes("type=for_you"))
    );
  }, [location.pathname, location.search]);

  // Navigation handlers
  const handleHomeClick = () => navigate("/home");
  const handleExploreClick = () => navigate("/explore");
  const handleForYouClick = () => navigate("/feed?type=for_you");
  const handleTrendingClick = () => navigate("/trending");
  const handleCreatePostClick = () => navigate("/create-post");
  const handleNotificationClick = () => navigate("/notifications");
  const handleSettingsClick = () => navigate("/settings");

  // Notification methods omitted for brevity (keep your original ones here)
  const getAdmireMessage = useCallback((admireType) => {
    /* your original logic */
  }, []);
  const getNotificationMessage = useCallback(
    (notification) => {
      /* your original logic */
    },
    [getAdmireMessage],
  );

  return (
    <nav className="lg:fixed top-0 left-0 right-0 bg-[#f8f9fa] z-[50]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between h-[56px] sm:h-[60px] lg:h-[64px]">
        {/* Left Section*/}
        <div className="flex items-center">
          {showToggle && (
  <div className="flex md:hidden items-center bg-white rounded-full shadow-sm border border-gray-200 p-1.5 gap-1.5 w-full max-w-[280px] mx-auto">
    
    <button
      type="button"
      onClick={handleForYouClick}
      className={`flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-full transition-all duration-300 ease-out font-medium text-sm flex-1 ${
        activeTab === "for-you"
          ? "bg-gray-200 text-gray-900 font-semibold border border-gray-300 shadow-sm"
          : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
      }`}
    >
      <Target size={15} />
      <span className="whitespace-nowrap">For You</span>
    </button>

    <button
      type="button"
      onClick={handleExploreClick}
      className={`flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-full transition-all duration-300 ease-out font-medium text-sm flex-1 ${
        activeTab === "explore"
          ? "bg-gray-200 text-gray-900 font-semibold border border-gray-300 shadow-sm"
          : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
      }`}
    >
      <Search size={15} />
      <span className="whitespace-nowrap">Explore</span>
    </button>

  </div>
)}

          {(!showToggle || windowSize >= 768) && (
            <div
              className={`flex items-center space-x-3 ${showToggle ? "md:flex hidden" : ""}`}
            >
              <h1
                className="wryven-font text-4xl cursor-pointer"
                onClick={() => navigate("/home")}
              >
                Wryven
              </h1>
            </div>
          )}
        </div>

        {/* Center Navigation - UI RESTORED */}
        <div className="hidden lg:flex items-center gap-0.5 xl:gap-1 bg-white px-1.5 xl:px-2 py-1 rounded-full shadow-sm border border-gray-200">
          {[
            { id: "home", label: "Home", icon: Home, action: handleHomeClick },
            {
              id: "explore",
              label: "Explore",
              icon: Search,
              action: handleExploreClick,
            },
            {
              id: "for-you",
              label: "For You",
              icon: Target,
              action: handleForYouClick,
            },
            {
              id: "trending",
              label: "Trending",
              icon: TrendingUp,
              action: handleTrendingClick,
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={tab.action}
              type="button"
              className={`flex items-center gap-1 xl:gap-2 px-2 lg:px-3 xl:px-4 py-1.5 lg:py-2 rounded-full transition-all duration-200 cursor-pointer ${activeTab === tab.id
                  ? "text-gray-800 font-semibold stroke-[2]"
                  : "text-gray-500 hover:text-gray-700 stroke-[1.5]"
                } hover:bg-gray-100`}
            >
              <tab.icon
                size={16}
                strokeWidth={activeTab === tab.id ? 2 : 1.5}
              />
              <span className="text-xs lg:text-sm font-medium">
                {tab.label}
              </span>
            </button>
          ))}
        </div>

        {/* Right Section - UI RESTORED */}
        <div className="flex items-center gap-2 lg:gap-3">
          <button
            type="button"
            onClick={handleCreatePostClick}
            className="p-2 rounded-full bg-white hover:bg-gray-100 transition-colors shadow-sm hover:shadow-md cursor-pointer"
          >
            <Plus size={20} className="text-black" />
          </button>

          <button
            type="button"
            onClick={handleNotificationClick}
            className="relative p-2 rounded-full bg-white hover:bg-gray-100 transition-colors shadow-sm hover:shadow-md cursor-pointer"
          >
            <Bell size={20} className="text-black" />
            {showNotificationDot && (
              <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white animate-pulse" />
            )}
          </button>

          <button
            type="button"
            onClick={handleSettingsClick}
            className="p-2 rounded-full bg-white hover:bg-gray-100 transition-colors shadow-sm hover:shadow-md cursor-pointer"
          >
            <Settings size={20} className="text-black" />
          </button>
        </div>
      </div>
    </nav>
  );
};

export default FeedNavbar;
